import type { Currencies } from 'src/types/common/Currencies';
import type { ImmutableBoats, ImmutableSearchBoatDetails } from 'src/types/boat/BoatDetail';
import type { ImmutableOffer } from 'src/types/offer/Offer';
import type { ReduxState } from 'src/types/reduxState';
import type { ImmutableTrip } from 'src/types/trips/Trips';
import type { ImmutableUser, UserRole } from 'src/types/user/User';
import type { AddonsSlice } from 'src/inbox/ducks/paymentAddons';

export const getCurrencies = ({ currencies }: ReduxState): Currencies => currencies;

export const getBoat = ({ boat }: ReduxState): ImmutableSearchBoatDetails => boat;

export const getPaymentAddons = ({ paymentAddons }: ReduxState): AddonsSlice => paymentAddons;

export const getUserBoats = ({ user }: ReduxState): ImmutableBoats => user.get('boats');

export const getOffer = ({ offer }: ReduxState): ImmutableOffer => offer;

export const getUser = ({ user }: ReduxState): ImmutableUser => user;

/**
 * Determines the authenticated user role based on the user's profile properties.
 *
 * This represents the user's account-level role (from /api/v4/auth/user/),
 * which differs from trip-specific roles where an owner may act as a renter
 * when booking their own trip.
 *
 * Role precedence (highest to lowest):
 * 1. Owner - has owner_approved property (takes precedence over captain)
 * 2. Captain - has is_captain property
 * 3. Renter - default for authenticated users
 *
 * @returns The highest priority role for the user, or null if no user
 */
export const getAuthenticatedUserRole = ({ user }: ReduxState): UserRole | null => {
  if (!user) {
    return null;
  }
  // if user is an owner (regardless of captain status), assign 'owner' role
  if (user.get('owner_approved')) {
    return 'owner';
  }
  // if user is a captain, assign 'captain' role
  if (user.get('is_captain')) {
    return 'captain';
  }
  // authenticated user, assign 'renter' role by default
  return 'renter';
};

export const getCountryCode = ({ countryCode }: ReduxState): string => countryCode;

export const getLoading = ({ loading }: ReduxState) => loading;

export const getTransaction = ({ transaction }: ReduxState) => transaction;

export const getTrip = (tripId: string) => ({ trips }: ReduxState): ImmutableTrip => (
  trips.get('results').find((t) => t?.get('pk') === Number(tripId))
);

export const getTrips = ({ trips }: ReduxState) => trips;

export const getOccasions = ({ occasions }: ReduxState) => occasions;

export const isPaymentMethodSet = ({ user }: ReduxState) => (
  !!(user.get('is_owner') && user.get('has_payout_method'))
);

export const getLinkedCaptains = ({ linkedCaptains }: ReduxState) => linkedCaptains;

export const getMobileNativeWebView = ({ mobileNativeWebView }: ReduxState) => mobileNativeWebView;
